<template>
  <v-container fluid class="al">
    <div class="al-head">
      <h1 class="al-title"><v-icon>mdi-history</v-icon> {{ $t('auditLogs.title') }}</h1>
      <v-btn small outlined color="primary" @click="exportCsv" :disabled="!items.length">
        <v-icon small left>mdi-download</v-icon>{{ $t('auditLogs.exportCsv') }}
      </v-btn>
    </div>

    <div class="al-filters">
      <div class="al-search">
        <v-icon small class="al-search-icon">mdi-magnify</v-icon>
        <input
          v-model="search"
          type="text"
          class="al-search-input"
          :placeholder="$t('auditLogs.searchPlaceholder')"
          @keyup.enter="fetchData(1)"
        >
        <v-btn v-if="search" icon x-small @click="search = ''; fetchData(1)"><v-icon x-small>mdi-close</v-icon></v-btn>
      </div>

      <v-select
        v-model="filters.action"
        :items="actionOptions"
        item-text="label"
        item-value="action"
        :label="$t('auditLogs.action')"
        clearable dense outlined hide-details
        @change="fetchData(1)"
      />
      <v-select
        v-model="filters.status"
        :items="statusOptions"
        :label="$t('common.status')"
        clearable dense outlined hide-details
        @change="fetchData(1)"
      />
      <v-select
        v-model="filters.role"
        :items="roleOptions"
        item-text="text"
        item-value="value"
        :label="$t('auditLogs.role')"
        clearable dense outlined hide-details
        @change="fetchData(1)"
      />
      <v-autocomplete
        v-model="filters.userId"
        :items="adminOptions"
        item-text="label"
        item-value="value"
        label="Pracownik"
        clearable dense outlined hide-details
        :loading="loadingAdmins"
        @change="fetchData(1)"
      />
      <v-autocomplete
        v-model="filters.companyId"
        :items="companyOptions"
        item-text="name"
        item-value="id"
        label="Firma"
        clearable dense outlined hide-details
        :loading="loadingCompanies"
        @change="fetchData(1)"
      />
      <v-text-field
        v-model="filters.from"
        type="date"
        :label="$t('auditLogs.from')"
        dense outlined hide-details
        @change="fetchData(1)"
      />
      <v-text-field
        v-model="filters.to"
        type="date"
        :label="$t('auditLogs.to')"
        dense outlined hide-details
        @change="fetchData(1)"
      />
      <v-btn small text @click="clearFilters" v-if="hasFilters">
        <v-icon small left>mdi-filter-off-outline</v-icon>{{ $t('auditLogs.clear') }}
      </v-btn>
    </div>

    <div v-if="hasFilters" class="al-chips">
      <v-chip v-if="search" small close @click:close="search = ''; fetchData(1)">
        <v-icon small left>mdi-magnify</v-icon>„{{ search }}"
      </v-chip>
      <v-chip v-if="filters.action" small close @click:close="filters.action = null; fetchData(1)">
        Akcja: {{ actionLabel(filters.action) }}
      </v-chip>
      <v-chip v-if="filters.status" small close @click:close="filters.status = null; fetchData(1)">
        Status: {{ filters.status }}
      </v-chip>
      <v-chip v-if="filters.role" small close @click:close="filters.role = null; fetchData(1)">
        Rola: {{ roleLabel(filters.role) }}
      </v-chip>
      <v-chip v-if="filters.userId" small close @click:close="filters.userId = null; fetchData(1)">
        Pracownik: {{ adminLabel(filters.userId) }}
      </v-chip>
      <v-chip v-if="filters.companyId" small close @click:close="filters.companyId = null; fetchData(1)">
        Firma: {{ companyLabel(filters.companyId) }}
      </v-chip>
      <v-chip v-if="filters.from" small close @click:close="filters.from = null; fetchData(1)">
        Od: {{ filters.from }}
      </v-chip>
      <v-chip v-if="filters.to" small close @click:close="filters.to = null; fetchData(1)">
        Do: {{ filters.to }}
      </v-chip>
    </div>

    <div v-if="loading" class="al-state">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="items.length === 0" class="al-state">
      <v-icon large color="grey">mdi-database-off-outline</v-icon>
      <div>{{ $t('auditLogs.noEntries') }}</div>
    </div>

    <div v-else class="al-table-wrap">
      <table class="al-table">
        <thead>
          <tr>
            <th>{{ $t('auditLogs.dateTime') }}</th>
            <th>{{ $t('auditLogs.user') }}</th>
            <th>Firma</th>
            <th>{{ $t('auditLogs.role') }}</th>
            <th>{{ $t('auditLogs.action') }}</th>
            <th>{{ $t('auditLogs.target') }}</th>
            <th>{{ $t('auditLogs.details') }}</th>
            <th>{{ $t('common.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in items"
            :key="entry.id"
            class="al-row"
            :class="{ 'al-row--failure': entry.status === 'FAILURE' }"
            @click="openDetail(entry)"
          >
            <td class="al-cell-time">{{ formatDate(entry.timestamp) }}</td>
            <td class="al-cell-user">
              <div class="al-user-name">{{ entry.userName }} {{ entry.userSurname }}</div>
              <div class="al-user-email">{{ entry.userEmail }}</div>
            </td>
            <td class="al-cell-company">{{ entry.companyName || '—' }}</td>
            <td><span class="al-role" :class="'al-role--' + entry.userRole">{{ entry.userRole }}</span></td>
            <td><span class="al-action">{{ entry.label }}</span></td>
            <td class="al-cell-target">{{ entry.targetDisplay || '—' }}</td>
            <td class="al-cell-details" :title="detailsLabel(entry)">{{ detailsLabel(entry) }}</td>
            <td>
              <span class="al-status" :class="'al-status--' + entry.status">
                <v-icon x-small>{{ statusIcon(entry.status) }}</v-icon>
                {{ entry.status }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="al-pagination" v-if="total > 0">
      <div>Wyświetlono {{ (page - 1) * limit + 1 }}–{{ Math.min(page * limit, total) }} z {{ total }} wpisów</div>
      <div class="al-page-controls">
        <v-btn small outlined :disabled="page <= 1" @click="fetchData(page - 1)">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <span>strona {{ page }} / {{ totalPages }}</span>
        <v-btn small outlined :disabled="page >= totalPages" @click="fetchData(page + 1)">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
        <v-select
          v-model="limit"
          :items="[25, 50, 100, 200]"
          dense outlined hide-details
          style="max-width: 90px"
          @change="fetchData(1)"
        />
      </div>
    </div>

    <!-- Detail drawer -->
    <v-dialog content-class="dark-form-dialog" v-model="detailOpen" max-width="640">
      <v-card v-if="detailEntry">
        <v-card-title>
          {{ detailEntry.label }}
          <v-spacer />
          <v-btn icon @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <table class="al-detail-table">
            <tr><th>{{ $t('common.date') }}:</th><td>{{ formatDate(detailEntry.timestamp) }}</td></tr>
            <tr><th>{{ $t('auditLogs.user') }}:</th><td>{{ detailEntry.userName }} {{ detailEntry.userSurname }} ({{ detailEntry.userEmail }})</td></tr>
            <tr v-if="detailEntry.companyName"><th>Firma:</th><td>{{ detailEntry.companyName }}</td></tr>
            <tr><th>{{ $t('auditLogs.role') }}:</th><td>{{ detailEntry.userRole }}</td></tr>
            <tr><th>{{ $t('auditLogs.action') }}:</th><td><code>{{ detailEntry.action }}</code></td></tr>
            <tr><th>{{ $t('auditLogs.endpoint') }}:</th><td><code>{{ detailEntry.method }} {{ detailEntry.path }}</code></td></tr>
            <tr v-if="detailEntry.targetType"><th>{{ $t('auditLogs.targetType') }}:</th><td>{{ detailEntry.targetType }}</td></tr>
            <tr v-if="detailEntry.targetId"><th>{{ $t('auditLogs.targetId') }}:</th><td><code>{{ detailEntry.targetId }}</code></td></tr>
            <tr v-if="detailEntry.targetDisplay"><th>{{ $t('auditLogs.targetDisplay') }}:</th><td>{{ detailEntry.targetDisplay }}</td></tr>
            <tr><th>{{ $t('auditLogs.details') }}:</th><td><v-btn v-if="detailGenerationReport" small outlined color="primary" @click="generationReportOpen = true">{{ $t('generationReport.openReport') }}</v-btn><span v-else>{{ detailEntry.details }}</span></td></tr>
            <tr><th>{{ $t('common.status') }}:</th><td>{{ detailEntry.status }}</td></tr>
            <tr v-if="detailEntry.ip"><th>{{ $t('auditLogs.ip') }}:</th><td><code>{{ detailEntry.ip }}</code></td></tr>
          </table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <settlement-generation-report-dialog v-model="generationReportOpen" :report="detailGenerationReport" />
  </v-container>
</template>

<script>
import SettlementGenerationReportDialog from '@/components/SettlementGenerationReportDialog.vue';
import { parseGenerationReport, generationSummary } from '@/utils/settlementGenerationReport';
const axios = require('axios');

export default {
  name: 'AuditLogsPage',
  components: { SettlementGenerationReportDialog },

  data: () => ({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    loading: false,

    search: '',
    filters: { action: null, status: null, role: null, userId: null, companyId: null, from: null, to: null },

    adminOptions: [],
    loadingAdmins: false,
    companyOptions: [],
    loadingCompanies: false,
    actionOptions: [],
    statusOptions: ['SUCCESS', 'FAILURE', 'WARNING'],
    roleOptions: [
      { text: 'Wszystkie role', value: null },
      { text: 'Head Admin', value: 'headadmin' },
      { text: 'Super Admin', value: 'superadmin' },
      { text: 'Office', value: 'office' },
      { text: 'Manager', value: 'user' },
      { text: 'System (cron / queue)', value: 'system' },
      { text: 'Driver', value: 'driver' },
    ],

    detailOpen: false,
    detailEntry: null,
    generationReportOpen: false,
  }),

  computed: {
    detailGenerationReport() { return this.entryGenerationReport(this.detailEntry); },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
    hasFilters() {
      return !!(
        this.search ||
        this.filters.action ||
        this.filters.status ||
        this.filters.role ||
        this.filters.userId ||
        this.filters.companyId ||
        this.filters.from ||
        this.filters.to
      );
    },
  },

  mounted() {
    this.fetchCatalog();
    this.fetchAdmins();
    this.fetchCompanies();
    this.fetchData(1);
  },

  methods: {
    entryGenerationReport(entry) {
      return entry?.action === 'SETTLEMENT_GENERATION_REPORT' ? parseGenerationReport(entry.details) : null;
    },
    detailsLabel(entry) {
      const report = this.entryGenerationReport(entry);
      return report ? generationSummary(report, this.$t.bind(this)) : entry.details;
    },
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(path) { return process.env.VUE_APP_ROOT_API + path; },

    async fetchCatalog() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('audit-log/catalog'), headers: this.authHeaders() });
        this.actionOptions = res.data || [];
      } catch (e) { this.actionOptions = []; }
    },
    async fetchAdmins() {
      this.loadingAdmins = true;
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('company-admins'), headers: this.authHeaders() });
        const list = res.data?.data || res.data || [];
        this.adminOptions = list.map((a) => ({
          label: `${a.firstName || ''} ${a.lastName || ''}`.trim() + ` — ${a.email}`,
          value: a.id,
        }));
      } catch (e) { this.adminOptions = []; }
      this.loadingAdmins = false;
    },
    adminLabel(id) {
      const a = this.adminOptions.find(x => x.value === id);
      return a ? a.label : id;
    },
    async fetchCompanies() {
      this.loadingCompanies = true;
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('audit-log/companies'), headers: this.authHeaders() });
        this.companyOptions = res.data || [];
      } catch (e) { this.companyOptions = []; }
      this.loadingCompanies = false;
    },
    companyLabel(id) {
      const c = this.companyOptions.find(x => x.id === id);
      return c ? c.name : id;
    },
    async fetchData(page) {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('limit', this.limit);
        if (this.search) params.set('search', this.search);
        if (this.filters.action) params.set('action', this.filters.action);
        if (this.filters.status) params.set('status', this.filters.status);
        if (this.filters.role) params.set('role', this.filters.role);
        if (this.filters.userId) params.set('userId', this.filters.userId);
        if (this.filters.companyId) params.set('companyId', this.filters.companyId);
        if (this.filters.from) params.set('from', this.filters.from);
        if (this.filters.to) params.set('to', this.filters.to);
        const res = await axios({
          method: 'get',
          url: this.apiUrl('audit-log?' + params.toString()),
          headers: this.authHeaders(),
        });
        this.items = res.data.items || [];
        this.total = res.data.total || 0;
        this.page = res.data.page || 1;
      } catch (e) {
        console.error('Audit log fetch failed:', e);
        this.items = [];
        this.total = 0;
      }
      this.loading = false;
    },

    clearFilters() {
      this.search = '';
      this.filters = { action: null, status: null, role: null, userId: null, companyId: null, from: null, to: null };
      this.fetchData(1);
    },

    openDetail(entry) {
      this.detailEntry = entry;
      this.detailOpen = true;
    },

    actionLabel(action) {
      const a = this.actionOptions.find(x => x.action === action);
      return a ? a.label : action;
    },
    roleLabel(role) {
      const r = this.roleOptions.find(x => x.value === role);
      return r ? r.text : role;
    },
    statusIcon(s) {
      if (s === 'SUCCESS') return 'mdi-check-circle';
      if (s === 'FAILURE') return 'mdi-alert-circle';
      return 'mdi-alert';
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleString('pl-PL', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },

    exportCsv() {
      const headers = ['Data', 'Imię', 'Nazwisko', 'Email', 'Firma', 'Rola', 'Akcja', 'Dotyczy', 'Szczegóły', 'Status', 'IP'];
      const rows = this.items.map(e => [
        this.formatDate(e.timestamp),
        e.userName, e.userSurname, e.userEmail,
        e.companyName || '',
        e.userRole,
        e.label,
        e.targetDisplay || '',
        e.details,
        e.status,
        e.ip || '',
      ]);
      const csv = [headers, ...rows].map(r =>
        r.map(c => '"' + String(c == null ? '' : c).replace(/"/g, '""') + '"').join(',')
      ).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-log-' + new Date().toISOString().slice(0, 10) + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.al {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.al-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px;
}
.al-title {
  font-size: 28px; font-weight: 700; margin: 0;
  display: flex; align-items: center; gap: 10px; color: #FFFFFF;
}

.al-filters {
  display: grid;
  grid-template-columns: 2fr repeat(7, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}
@media (max-width: 1100px) { .al-filters { grid-template-columns: 1fr 1fr 1fr; } }

.al-search {
  display: flex; align-items: center; gap: 6px;
  background: #232838; border: 1px solid #2A3142;
  border-radius: 8px; padding: 0 10px; height: 40px;
}
.al-search-input {
  flex: 1; border: 0; outline: 0; background: transparent;
  font-size: 14px; color: #E6EAF2;
}
.al-search-input::placeholder { color: #6B7494; }
.al-search-icon { color: #94A0B8; }

.al-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }

.al-state {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 60px 20px; color: #94A0B8; text-align: center;
}

.al-table-wrap { overflow: auto; background: #232838; border: 1px solid #2A3142; border-radius: 12px; }
.al-table { width: 100%; border-collapse: collapse; font-size: 13px; color: #E6EAF2; }
.al-table th {
  background: #1F2433; padding: 12px; text-align: left;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em;
  color: #94A0B8; position: sticky; top: 0; z-index: 1;
  border-bottom: 1px solid #2A3142;
}
.al-table td {
  padding: 10px 12px; border-bottom: 1px solid #2A3142; vertical-align: top;
  color: #E6EAF2;
}
.al-row { cursor: pointer; transition: background-color .1s; }
.al-row:hover { background: rgba(84, 120, 255, 0.06); }
.al-row--failure { background: rgba(220, 38, 38, 0.08); }
.al-row--failure:hover { background: rgba(220, 38, 38, 0.14); }

.al-cell-time { white-space: nowrap; color: #94A0B8; font-variant-numeric: tabular-nums; }
.al-user-name { font-weight: 600; color: #E6EAF2; }
.al-user-email { font-size: 11px; color: #6B7494; }
.al-cell-company { white-space: nowrap; color: #C7CEDC; font-weight: 600; }
.al-cell-target { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.al-cell-details { max-width: 380px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #94A0B8; }

.al-role {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
  background: #eceff3; color: #4b5563;
}
.al-role--superadmin { background: #fef3c7; color: #92400e; }
.al-role--admin      { background: #dbeafe; color: #1e40af; }
.al-role--office     { background: #e0e7ff; color: #3730a3; }
.al-role--user       { background: #dcfce7; color: #166534; }
.al-role--driver     { background: #f3e8ff; color: #6b21a8; }
.al-role--unknown    { background: #f3f4f6; color: #6b7280; }

.al-action { font-weight: 500; }

.al-status {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
}
.al-status--SUCCESS { background: #dcfce7; color: #166534; }
.al-status--FAILURE { background: #fee2e2; color: #991b1b; }
.al-status--WARNING { background: #fef3c7; color: #854d0e; }

.al-pagination {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 12px; font-size: 13px; color: #94A0B8;
}
.al-page-controls { display: flex; align-items: center; gap: 10px; }

.al-detail-table {
  width: 100%;
  border-collapse: collapse;
  background: #232838;
  border: 1px solid #2A3142;
  border-radius: 8px;
  overflow: hidden;
}
.al-detail-table tr { border-bottom: 1px solid #2A3142; }
.al-detail-table tr:last-child { border-bottom: 0; }
.al-detail-table tr:nth-child(even) { background: #1F2433; }
.al-detail-table th {
  text-align: left;
  padding: 10px 12px;
  font-weight: 700;
  color: #E6EAF2;
  width: 140px;
  vertical-align: top;
  font-size: 13px;
  background: #1F2433;
  border-right: 1px solid #2A3142;
  letter-spacing: .2px;
}
.al-detail-table td {
  padding: 10px 12px;
  font-size: 13px;
  color: #E6EAF2;
  word-break: break-word;
}
.al-detail-table code {
  background: #1F2433;
  color: #E6EAF2;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  border: 1px solid #2A3142;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.al >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.al >>> .v-text-field input,
.al >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.al >>> .v-label {
  color: #94A0B8 !important;
}

/* Dark theme support */
.theme--dark .al-detail-table {
  background: #1e1e1e;
  border-color: rgba(255,255,255,.15);
}
.theme--dark .al-detail-table tr { border-bottom-color: rgba(255,255,255,.08); }
.theme--dark .al-detail-table tr:nth-child(even) { background: #252830; }
.theme--dark .al-detail-table th {
  background: #2b2f38;
  color: rgba(255,255,255,.92);
  border-right-color: rgba(255,255,255,.1);
}
.theme--dark .al-detail-table td { color: rgba(255,255,255,.92); }
.theme--dark .al-detail-table code {
  background: #2b2f38;
  color: #e5e7eb;
  border-color: rgba(255,255,255,.15);
}
</style>
